import { makeAutoObservable } from 'mobx';

import { ChallengeProfileInfo } from '../interfaces/ChallengeProfileInfo';
import { resetAllStore } from '../../shared/stores/resetAllStore';
import { challengeService } from '../services/challengeService';
import { Mode } from '../../general-types';
import {
  CHALLENGE_STATUS,
  ChallengeStatusFlags,
  ChallengeStatusType,
} from '../constants/challengeStatus';
import {
  CHALLENGE_SECTION,
  ChallengeSectionFlags,
  ChallengeSectionKey,
} from '../constants/challengeSection';
import { getFlagsObject } from '../../shared/utils/sortingAndGroupingUtil';

export type ChallengeProfileForms = 'about' | 'importantDates' | 'prizes' | 'faq';

class ChallengeProfileStore {
  challengeProfileInfo: ChallengeProfileInfo | null = null;
  formsModeConfig: Record<ChallengeProfileForms, Mode> = {
    about: 'VIEW',
    importantDates: 'VIEW',
    prizes: 'VIEW',
    faq: 'VIEW',
  };
  statusFlags: ChallengeStatusFlags = {};
  invalidSections: ChallengeSectionKey[] = [];
  invalidSectionFlags: ChallengeSectionFlags = {};
  showPublishedModal = false;

  /**
   * Incrementing version forces GeneralTab remount via React key,
   * causing all form widgets to re-fetch their data from the backend.
   * Use `increaseFormVersion()` after actions that mutate form-related data (e.g. unpublish).
   */
  formVersion = 0;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  async getChallengeProfileInfo(challengeId: number) {
    const challengeProfileInfo =
      await challengeService.getChallengeProfileInfo(challengeId);

    this.setChallengeProfileInfo(challengeProfileInfo);
    this.setStatusFlags(challengeProfileInfo.status);
  }

  setDefaultValues() {
    this.challengeProfileInfo = null;
    this.formsModeConfig = {
      about: 'VIEW',
      importantDates: 'VIEW',
      prizes: 'VIEW',
      faq: 'VIEW',
    };
    this.statusFlags = {};
    this.invalidSections = [];
    this.invalidSectionFlags = {};
    this.showPublishedModal = false;
    this.formVersion = 0;
  }

  async publishChallenge(challengeId: number) {
    await challengeService.publishChallenge(challengeId);
    await this.getChallengeProfileInfo(challengeId);
    this.setShowPublishedModal(true);
  }

  async unpublishChallenge(challengeId: number) {
    await challengeService.unpublishChallenge(challengeId);
    await this.getChallengeProfileInfo(challengeId);
    await this.getChallengeStatusDetails(challengeId);
    this.increaseFormVersion();
  }

  async getChallengeStatusDetails(challengeId: number) {
    const { invalidSections } =
      await challengeService.getChallengeStatusDetails(challengeId);

    this.setInvalidSections(invalidSections);
  }

  private increaseFormVersion() {
    this.formVersion++;
  }

  setShowPublishedModal(value: boolean) {
    this.showPublishedModal = value;
  }

  closePublishedModal() {
    this.showPublishedModal = false;
  }

  private setChallengeProfileInfo(challengeProfileInfo: ChallengeProfileInfo) {
    this.challengeProfileInfo = challengeProfileInfo;
  }

  updateChallengeProfileInfo(challengeProfileInfo: Partial<ChallengeProfileInfo>) {
    this.challengeProfileInfo = {
      ...this.challengeProfileInfo,
      ...challengeProfileInfo,
    } as ChallengeProfileInfo;
  }

  setFormMode(form: ChallengeProfileForms, mode: Mode) {
    this.formsModeConfig[form] = mode;
  }

  getIsEditing() {
    return Object.values(this.formsModeConfig).some((mode) => mode === 'EDIT');
  }

  private setStatusFlags(status: ChallengeStatusType) {
    this.statusFlags = getFlagsObject(CHALLENGE_STATUS, [status]);
  }

  private setInvalidSections(invalidSections: ChallengeSectionKey[]) {
    this.invalidSections = invalidSections;
    this.invalidSectionFlags = getFlagsObject(CHALLENGE_SECTION, invalidSections);
  }
}

const challengeProfileStore = new ChallengeProfileStore();
resetAllStore.addResetMethod(challengeProfileStore.setDefaultValues);

export { challengeProfileStore, ChallengeProfileStore };
